/**
 * Profile > My Agents.
 *
 * A user manages their workspace's agents and the access keys under each.
 * Per [[0020 - agent-native-architecture-agents-external-to-platform]] only
 * humans manage agents/keys — the API enforces this, and this screen is the
 * human surface for it.
 *
 * Keys carry a per-app scope (ADR 0023 §2). The scope multi-select is sourced
 * from the workspace's enabled apps (the session app list). A freshly minted
 * key shows its raw token EXACTLY ONCE with a copy affordance.
 */

import { useState } from 'react';
import { useSession } from '../../api/auth-hooks';
import {
  useAgents,
  useCreateAgent,
  useUpdateAgent,
  useArchiveAgent,
  useAgentKeys,
  useIssueKey,
  useEditKey,
  useRevokeKey,
  type AgentDto,
  type AgentKeyDto,
} from '../../api/agents';
import { FormModal } from '../../components/FormModal';
import formStyles from '../../components/FormModal.module.css';
import { TokenReveal } from '../../components/TokenReveal';
import styles from './Agents.module.css';

export function AgentsPage() {
  const session = useSession();
  const enabledApps = (session.data?.apps ?? []).map((a) => ({
    slug: a.slug as string,
    name: a.name,
  }));

  const agentsQ = useAgents();
  const agents = agentsQ.data?.agents ?? [];

  const [createOpen, setCreateOpen] = useState(false);
  const [editAgent, setEditAgent] = useState<AgentDto | null>(null);
  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(null);

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>My agents</h1>
          <p className={styles.sub}>
            Agents are external services that act in this workspace through the
            public API. Issue a scoped access key for each agent; revoke it any
            time. You — never an agent — manage these.
          </p>
        </div>
        <button
          type="button"
          className="kos-btn kos-btn-primary"
          onClick={() => setCreateOpen(true)}
        >
          New agent
        </button>
      </header>

      {agentsQ.isLoading && <div className={styles.empty}>Loading…</div>}
      {agentsQ.isError && (
        <div className={styles.empty}>
          Couldn’t load your agents. {(agentsQ.error as Error)?.message ?? ''}
        </div>
      )}
      {!agentsQ.isLoading && !agentsQ.isError && agents.length === 0 && (
        <div className={styles.empty}>
          No agents yet. Create one to mint an access key.
        </div>
      )}

      <div className={styles.list}>
        {agents.map((agent) => (
          <AgentCard
            key={agent.id}
            agent={agent}
            expanded={selectedAgentId === agent.id}
            onToggle={() =>
              setSelectedAgentId((cur) => (cur === agent.id ? null : agent.id))
            }
            onEdit={() => setEditAgent(agent)}
            enabledApps={enabledApps}
          />
        ))}
      </div>

      {createOpen && (
        <AgentFormModal open onClose={() => setCreateOpen(false)} />
      )}
      {editAgent && (
        <AgentFormModal
          open
          agent={editAgent}
          onClose={() => setEditAgent(null)}
        />
      )}
    </div>
  );
}

// ─── Agent card with collapsible keys panel ───────────────────────────

function AgentCard({
  agent,
  expanded,
  onToggle,
  onEdit,
  enabledApps,
}: {
  agent: AgentDto;
  expanded: boolean;
  onToggle: () => void;
  onEdit: () => void;
  enabledApps: { slug: string; name: string }[];
}) {
  const keysQ = useAgentKeys(expanded ? agent.id : null);
  const keys = keysQ.data?.keys ?? [];

  return (
    <section className={styles.card}>
      <div className={styles.cardHead}>
        <button
          type="button"
          className={styles.cardToggle}
          onClick={onToggle}
          aria-expanded={expanded}
        >
          <span className={styles.caret} aria-hidden>
            {expanded ? '▾' : '▸'}
          </span>
          <span className={styles.cardName}>{agent.label}</span>
          <StatusBadge status={agent.status} />
        </button>
        <div className={styles.cardActions}>
          <button type="button" className="kos-btn" onClick={onEdit}>
            Edit
          </button>
        </div>
      </div>
      {agent.description && (
        <p className={styles.cardDesc}>{agent.description}</p>
      )}

      {expanded && (
        <KeysPanel agentId={agent.id} keys={keys} loading={keysQ.isLoading} enabledApps={enabledApps} />
      )}
    </section>
  );
}

function StatusBadge({ status }: { status: AgentDto['status'] }) {
  return (
    <span className={`${styles.badge} ${styles[`badge_${status}`]}`}>
      {status}
    </span>
  );
}

// ─── Keys panel ───────────────────────────────────────────────────────

function KeysPanel({
  agentId,
  keys,
  loading,
  enabledApps,
}: {
  agentId: string;
  keys: AgentKeyDto[];
  loading: boolean;
  enabledApps: { slug: string; name: string }[];
}) {
  const [issueOpen, setIssueOpen] = useState(false);
  const [editKey, setEditKey] = useState<AgentKeyDto | null>(null);

  return (
    <div className={styles.keysPanel}>
      <div className={styles.keysHead}>
        <span className={styles.keysTitle}>Access keys</span>
        <button
          type="button"
          className="kos-btn kos-btn-primary"
          onClick={() => setIssueOpen(true)}
        >
          Issue key
        </button>
      </div>

      {loading && <div className={styles.keysEmpty}>Loading keys…</div>}
      {!loading && keys.length === 0 && (
        <div className={styles.keysEmpty}>No active keys.</div>
      )}

      {keys.length > 0 && (
        <table className={styles.keysTable}>
          <thead>
            <tr>
              <th>Label</th>
              <th>App scope</th>
              <th>Last used</th>
              <th className={styles.actionsCell} />
            </tr>
          </thead>
          <tbody>
            {keys.map((k) => (
              <KeyRow
                key={k.id}
                agentId={agentId}
                keyRow={k}
                onEdit={() => setEditKey(k)}
              />
            ))}
          </tbody>
        </table>
      )}

      {issueOpen && (
        <IssueKeyModal
          agentId={agentId}
          enabledApps={enabledApps}
          onClose={() => setIssueOpen(false)}
        />
      )}
      {editKey && (
        <EditKeyModal
          agentId={agentId}
          keyRow={editKey}
          enabledApps={enabledApps}
          onClose={() => setEditKey(null)}
        />
      )}
    </div>
  );
}

function ScopeChips({ appScope }: { appScope: string[] | null }) {
  if (appScope === null) {
    return <span className={styles.chipAll}>All apps</span>;
  }
  if (appScope.length === 0) {
    return <span className={styles.chipAll}>All apps</span>;
  }
  return (
    <span className={styles.chips}>
      {appScope.map((s) => (
        <span key={s} className={styles.chip}>
          {s}
        </span>
      ))}
    </span>
  );
}

function KeyRow({
  agentId,
  keyRow,
  onEdit,
}: {
  agentId: string;
  keyRow: AgentKeyDto;
  onEdit: () => void;
}) {
  const revoke = useRevokeKey(agentId, keyRow.id);
  const [confirming, setConfirming] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const lastUsed = keyRow.lastUsedAt
    ? new Date(keyRow.lastUsedAt).toLocaleString(undefined, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : 'never';

  return (
    <>
      <tr>
        <td>{keyRow.label}</td>
        <td>
          <ScopeChips appScope={keyRow.appScope} />
        </td>
        <td className={styles.muted}>{lastUsed}</td>
        <td className={styles.actionsCell}>
          <button
            type="button"
            className="kos-btn"
            onClick={() => setRegenerating(true)}
          >
            Regenerate
          </button>
          <button type="button" className="kos-btn" onClick={onEdit}>
            Edit
          </button>
          {confirming ? (
            <>
              <button
                type="button"
                className={styles.btnDanger}
                onClick={() => revoke.mutate()}
                disabled={revoke.isPending}
              >
                {revoke.isPending ? 'Revoking…' : 'Confirm'}
              </button>
              <button
                type="button"
                className="kos-btn"
                onClick={() => setConfirming(false)}
              >
                Cancel
              </button>
            </>
          ) : (
            <button
              type="button"
              className={styles.btnDangerGhost}
              onClick={() => setConfirming(true)}
            >
              Revoke
            </button>
          )}
        </td>
      </tr>
      {regenerating && (
        <RegenerateKeyModal
          agentId={agentId}
          keyRow={keyRow}
          onClose={() => setRegenerating(false)}
        />
      )}
    </>
  );
}

// ─── Create / edit agent ──────────────────────────────────────────────

function AgentFormModal({
  open,
  agent,
  onClose,
}: {
  open: boolean;
  agent?: AgentDto;
  onClose: () => void;
}) {
  const isEdit = !!agent;
  const create = useCreateAgent();
  const update = useUpdateAgent();
  const archive = useArchiveAgent();

  const [label, setLabel] = useState(agent?.label ?? '');
  const [description, setDescription] = useState(agent?.description ?? '');

  const pending = create.isPending || update.isPending || archive.isPending;
  const valid = label.trim().length > 0;
  const error =
    (create.error as Error | null)?.message ??
    (update.error as Error | null)?.message ??
    (archive.error as Error | null)?.message ??
    null;

  const submit = () => {
    if (!valid || pending) return;
    const payload = { label: label.trim(), description: description.trim() };
    if (isEdit && agent) {
      update.mutate({ id: agent.id, patch: payload }, { onSuccess: onClose });
    } else {
      create.mutate(payload, { onSuccess: onClose });
    }
  };

  return (
    <FormModal
      open={open}
      onClose={onClose}
      title={isEdit ? 'Edit agent' : 'New agent'}
      subtitle={
        isEdit
          ? undefined
          : 'Name the external service that will act in this workspace.'
      }
      submitLabel={isEdit ? 'Save changes' : 'Create agent'}
      submitDisabled={!valid || pending}
      onSubmit={submit}
      error={error}
      destructiveAction={
        isEdit && agent
          ? {
              label: archive.isPending ? 'Archiving…' : 'Archive agent',
              onClick: () =>
                archive.mutate(agent.id, { onSuccess: onClose }),
            }
          : undefined
      }
    >
      <div className={formStyles.field}>
        <label className={formStyles.fieldLabel} htmlFor="agent-label">
          Label
        </label>
        <input
          id="agent-label"
          className={formStyles.input}
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="e.g. Daily review bot"
          maxLength={80}
        />
      </div>
      <div className={formStyles.field}>
        <label className={formStyles.fieldLabel} htmlFor="agent-desc">
          Description (optional)
        </label>
        <textarea
          id="agent-desc"
          className={formStyles.textarea}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="What does this agent do?"
          maxLength={2000}
        />
      </div>
    </FormModal>
  );
}

// ─── Issue key (raw token shown once) ─────────────────────────────────

function ScopePicker({
  enabledApps,
  selected,
  onChange,
}: {
  enabledApps: { slug: string; name: string }[];
  selected: string[];
  onChange: (next: string[]) => void;
}) {
  const allMode = selected.length === 0;
  const toggle = (slug: string) => {
    onChange(
      selected.includes(slug)
        ? selected.filter((s) => s !== slug)
        : [...selected, slug],
    );
  };
  return (
    <div className={formStyles.field}>
      <span className={formStyles.fieldLabel}>App scope</span>
      <div className={formStyles.pills}>
        <button
          type="button"
          className={`${formStyles.pill} ${allMode ? formStyles.pillActive : ''}`}
          onClick={() => onChange([])}
        >
          All apps
        </button>
        {enabledApps.map((app) => (
          <button
            type="button"
            key={app.slug}
            className={`${formStyles.pill} ${
              selected.includes(app.slug) ? formStyles.pillActive : ''
            }`}
            onClick={() => toggle(app.slug)}
          >
            {app.name}
          </button>
        ))}
      </div>
      <span className={formStyles.fieldHint}>
        Leave “All apps” selected for an unrestricted key, or pick the specific
        apps this key may reach.
      </span>
    </div>
  );
}

function IssueKeyModal({
  agentId,
  enabledApps,
  onClose,
}: {
  agentId: string;
  enabledApps: { slug: string; name: string }[];
  onClose: () => void;
}) {
  const issue = useIssueKey(agentId);
  const [label, setLabel] = useState('');
  const [scope, setScope] = useState<string[]>([]);
  const [rawToken, setRawToken] = useState<string | null>(null);

  const valid = label.trim().length > 0;
  const error = (issue.error as Error | null)?.message ?? null;

  const submit = () => {
    if (!valid || issue.isPending) return;
    issue.mutate(
      { label: label.trim(), appScope: scope.length ? scope : undefined },
      { onSuccess: (res) => setRawToken(res.key) },
    );
  };

  // Once the token is minted we swap the form for a one-time reveal.
  if (rawToken) {
    return (
      <FormModal
        open
        onClose={onClose}
        title="Key created"
        subtitle="Copy this token now — it is shown only once and cannot be retrieved again."
        submitLabel="Done"
        onSubmit={onClose}
      >
        <TokenReveal token={rawToken} />
      </FormModal>
    );
  }

  return (
    <FormModal
      open
      onClose={onClose}
      title="Issue access key"
      submitLabel={issue.isPending ? 'Issuing…' : 'Issue key'}
      submitDisabled={!valid || issue.isPending}
      onSubmit={submit}
      error={error}
    >
      <div className={formStyles.field}>
        <label className={formStyles.fieldLabel} htmlFor="key-label">
          Label
        </label>
        <input
          id="key-label"
          className={formStyles.input}
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="e.g. production"
          maxLength={80}
        />
      </div>
      <ScopePicker enabledApps={enabledApps} selected={scope} onChange={setScope} />
    </FormModal>
  );
}

function EditKeyModal({
  agentId,
  keyRow,
  enabledApps,
  onClose,
}: {
  agentId: string;
  keyRow: AgentKeyDto;
  enabledApps: { slug: string; name: string }[];
  onClose: () => void;
}) {
  const edit = useEditKey(agentId, keyRow.id);
  const [label, setLabel] = useState(keyRow.label);
  const [scope, setScope] = useState<string[]>(keyRow.appScope ?? []);

  const valid = label.trim().length > 0;
  const error = (edit.error as Error | null)?.message ?? null;

  const submit = () => {
    if (!valid || edit.isPending) return;
    edit.mutate(
      { label: label.trim(), appScope: scope.length ? scope : null },
      { onSuccess: onClose },
    );
  };

  return (
    <FormModal
      open
      onClose={onClose}
      title="Edit key"
      subtitle="Change the label or app scope without re-issuing the token."
      submitLabel={edit.isPending ? 'Saving…' : 'Save changes'}
      submitDisabled={!valid || edit.isPending}
      onSubmit={submit}
      error={error}
    >
      <div className={formStyles.field}>
        <label className={formStyles.fieldLabel} htmlFor="edit-key-label">
          Label
        </label>
        <input
          id="edit-key-label"
          className={formStyles.input}
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          maxLength={80}
        />
      </div>
      <ScopePicker enabledApps={enabledApps} selected={scope} onChange={setScope} />
    </FormModal>
  );
}

// ─── Regenerate key (revoke-first, then re-issue; raw token shown once) ────

function RegenerateKeyModal({
  agentId,
  keyRow,
  onClose,
}: {
  agentId: string;
  keyRow: AgentKeyDto;
  onClose: () => void;
}) {
  const revoke = useRevokeKey(agentId, keyRow.id);
  const issue = useIssueKey(agentId);
  const [rawToken, setRawToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const pending = revoke.isPending || issue.isPending;

  // Revoke-first: the old token must be dead before a new one appears. If the
  // re-issue fails after the revoke, the agent is briefly keyless — surface a
  // clear, actionable error rather than silently leaving it broken.
  const regenerate = async () => {
    if (pending) return;
    setError(null);
    try {
      await revoke.mutateAsync();
    } catch {
      setError('Could not revoke the current token. Nothing changed — try again.');
      return;
    }
    try {
      const res = await issue.mutateAsync({
        label: keyRow.label,
        appScope: keyRow.appScope ?? undefined,
      });
      setRawToken(res.key);
    } catch {
      setError(
        'The old token was revoked, but issuing a new one failed. Close this and issue a new key manually.',
      );
    }
  };

  if (rawToken) {
    return (
      <FormModal
        open
        onClose={onClose}
        title="Token regenerated"
        subtitle="Copy the new token now — it is shown only once. The previous token no longer works."
        submitLabel="Done"
        onSubmit={onClose}
      >
        <TokenReveal token={rawToken} />
      </FormModal>
    );
  }

  return (
    <FormModal
      open
      onClose={pending ? () => {} : onClose}
      title="Regenerate token"
      subtitle={`This revokes the current token for "${keyRow.label}" immediately and issues a new one with the same scope. Any agent using the old token will stop working until you update it.`}
      submitLabel={pending ? 'Regenerating…' : 'Regenerate'}
      submitDisabled={pending}
      onSubmit={regenerate}
      error={error}
    >
      <p className={styles.cardDesc}>
        The new token keeps this key's label and app scope. You'll see it once
        on the next screen.
      </p>
    </FormModal>
  );
}
